import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { ApiRequestService } from './api-request.service';

export type AutosaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface AutosaveContext {
  /** Unique identifier (e.g., form ID or 'new'). */
  id: string;
  /** API endpoint (e.g., 'v2/user/assigned-forms/123'). */
  endpoint: string;
  /** HTTP method. */
  method: 'put' | 'post';
  /** Function to get the current form data payload. */
  getPayload: () => any;
  /** Optional callback for post-save logic (e.g., Form Builder create-to-update flow). */
  onSuccess?: (response: any) => void;
}

@Injectable({ providedIn: 'root' })
export class AutosaveService implements OnDestroy {

  private readonly DEBOUNCE_MS = 6000;
  private readonly SAVED_DISPLAY_MS = 6000;

  /** Observable state for the save indicator component. */
  state$ = new BehaviorSubject<AutosaveState>('idle');

  /** Whether there are unsaved changes pending. */
  hasPendingChanges = false;

  private debounceTimer: any = null;
  private savedTimer: any = null;
  private inFlight = false;
  private queued = false;
  private context: AutosaveContext | null = null;
  private destroyed$ = new Subject<void>();
  private inFlightPromise: Promise<void> | null = null;

  constructor(private api: ApiRequestService) {}

  ngOnDestroy(): void {
    this.cleanup();
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /** Register a form context for autosave. Cleans up any previous context. */
  register(context: AutosaveContext): void {
    this.cleanup();
    this.context = context;
    this.hasPendingChanges = false;
    this.state$.next('idle');
  }

  /** Call on every field change. Resets the debounce timer. */
  markDirty(): void {
    this.hasPendingChanges = true;
    this.resetDebounce();
  }

  /** Flush immediately (used on navigate-away or browser close). */
  flush(): Promise<void> {
    if (!this.hasPendingChanges || !this.context) {
      return Promise.resolve();
    }
    this.cancelDebounce();

    // If a save is already in flight, wait for it to complete (including any queued follow-up).
    if (this.inFlight) {
      this.queued = true;
      return this.inFlightPromise || Promise.resolve();
    }

    return this.dispatchSave();
  }

  /** Cancel autosave (used when explicit submit takes over). */
  cancel(): void {
    this.cancelDebounce();
    this.queued = false;
  }

  /** Mark the form as clean after a successful explicit save. */
  markClean(): void {
    this.hasPendingChanges = false;
    this.state$.next('idle');
  }

  /** Resume autosave after explicit submit completes/fails. */
  resume(): void {
    // No action needed; next markDirty() will restart the cycle.
  }

  /** Unregister and clean up all timers and state. */
  cleanup(): void {
    this.cancelDebounce();
    this.clearSavedTimer();
    this.context = null;
    this.inFlight = false;
    this.queued = false;
    this.inFlightPromise = null;
    this.hasPendingChanges = false;
    this.state$.next('idle');
  }

  // ---------------------------------------------------------------------------
  // Private methods
  // ---------------------------------------------------------------------------

  private resetDebounce(): void {
    this.cancelDebounce();
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.dispatchSave();
    }, this.DEBOUNCE_MS);
  }

  private cancelDebounce(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  private clearSavedTimer(): void {
    if (this.savedTimer) {
      clearTimeout(this.savedTimer);
      this.savedTimer = null;
    }
  }

  private dispatchSave(): Promise<void> {
    if (!this.context) {
      return Promise.resolve();
    }

    // If already in flight, queue and wait.
    if (this.inFlight) {
      this.queued = true;
      return this.inFlightPromise || Promise.resolve();
    }

    this.inFlight = true;
    this.clearSavedTimer();
    this.state$.next('saving');

    // Capture the context at dispatch time to guard against stale callbacks
    // if register() is called while this request is in flight.
    const dispatchedContext = this.context;
    const payload = dispatchedContext.getPayload();
    const { endpoint, method } = dispatchedContext;

    this.inFlightPromise = this.api.makeRequest(method, endpoint, payload, {}, {
      'X-Skip-Spinner': 'true'
    })
      .then((response: any) => {
        // Only update state and invoke callback if the context hasn't changed.
        if (this.context !== dispatchedContext) {
          return;
        }

        this.hasPendingChanges = false;
        this.state$.next('saved');

        // Transition from 'saved' back to 'idle' after SAVED_DISPLAY_MS.
        this.savedTimer = setTimeout(() => {
          if (this.state$.value === 'saved') {
            this.state$.next('idle');
          }
        }, this.SAVED_DISPLAY_MS);

        // Callback for post-save logic (e.g., Form Builder create-to-update flow).
        if (dispatchedContext.onSuccess) {
          dispatchedContext.onSuccess(response);
        }
      })
      .catch(() => {
        // Only update state if context hasn't changed.
        if (this.context !== dispatchedContext) {
          return;
        }
        // Leave hasPendingChanges = true so next markDirty() retries.
        this.state$.next('error');
      })
      .finally(() => {
        this.inFlight = false;

        // If a save was queued while in flight, dispatch it now with latest data.
        if (this.queued) {
          this.queued = false;
          this.inFlightPromise = this.dispatchSave();
        } else {
          this.inFlightPromise = null;
        }
      });

    return this.inFlightPromise;
  }
}
